import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  PhotoItem,
  WatermarkExtractionStatus
} from '../context/WatermarkBatchContext'
import { setPhotoPickerPhotos } from '../../../../actions/photoPicker'

/**
 * 照片操作的自定义 Hook
 * 提供照片的增删改查等操作方法
 */
export const usePhotoOperations = (
  photos: PhotoItem[],
  setPhotosState: React.Dispatch<React.SetStateAction<any[]>>,
  currentIndex: number,
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
) => {
  const dispatch = useDispatch()

  // 应用水印到所有照片
  const applyWatermarkToAll = useCallback(
    async (data: any[]) => {
      // 更新所有照片的水印数据
      setPhotosState((prevPhotos) => {
        return prevPhotos.map((photo) => ({
          ...photo,
          watermarkData: data
        }))
      })
    },
    [setPhotosState]
  )

  // 标准化照片数据，确保所有必要字段都有默认值
  const normalizePhotos = useCallback((photos: any[]): any[] => {
    return photos.map((photo) => ({
      ...photo,
      // 默认设置为提取状态，将通过接口进行检测
      watermarkStatus:
        photo.watermarkStatus || WatermarkExtractionStatus.EXTRACTING
    }))
  }, [])

  // 移除指定索引的照片
  const removePhoto = useCallback(
    (photoIndex: number) => {
      setPhotosState((prevPhotos) => {
        const newPhotos = prevPhotos.filter((_, index) => index !== photoIndex)

        // 如果移除的是当前照片或之前的照片，需要调整当前索引
        if (photoIndex <= currentIndex && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1)
        }

        // 同步更新Redux中的数据
        dispatch(setPhotoPickerPhotos(newPhotos, 'batch-editor'))

        return newPhotos
      })
    },
    [currentIndex, dispatch, setPhotosState, setCurrentIndex]
  )

  // 移除所有失败的照片（包括提取失败和提取超时）
  const removeFailedPhotos = useCallback(() => {
    setPhotosState((prevPhotos) => {
      const newPhotos = prevPhotos.filter((photo) => {
        const isFailed =
          photo.watermarkStatus === WatermarkExtractionStatus.FAILED
        const isExtractingTimeout =
          photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING_TIMEOUT
        return !isFailed && !isExtractingTimeout
      })

      // 计算需要调整的当前索引
      let newIndex = currentIndex
      const removedCount = prevPhotos.length - newPhotos.length

      // 如果当前照片被移除，调整当前索引
      const currentPhoto = prevPhotos[currentIndex]
      if (currentPhoto) {
        const isFailed =
          currentPhoto.watermarkStatus === WatermarkExtractionStatus.FAILED
        const isExtractingTimeout =
          currentPhoto.watermarkStatus ===
          WatermarkExtractionStatus.EXTRACTING_TIMEOUT

        if (isFailed || isExtractingTimeout) {
          newIndex = Math.max(0, currentIndex - removedCount)
        } else if (currentIndex > 0) {
          // 如果当前照片之前有照片被移除，调整索引
          let beforeCurrentRemoved = 0
          for (let i = 0; i < currentIndex; i++) {
            const photo = prevPhotos[i]
            const isFailed =
              photo.watermarkStatus === WatermarkExtractionStatus.FAILED
            const isExtractingTimeout =
              photo.watermarkStatus ===
              WatermarkExtractionStatus.EXTRACTING_TIMEOUT

            if (isFailed || isExtractingTimeout) {
              beforeCurrentRemoved++
            }
          }
          newIndex = currentIndex - beforeCurrentRemoved
        }
      }

      setCurrentIndex(newIndex)
      return newPhotos
    })
  }, [currentIndex, setPhotosState, setCurrentIndex])

  return {
    applyWatermarkToAll,
    normalizePhotos,
    removePhoto,
    removeFailedPhotos
  }
}
